// src/app/services/realtime-stt.service.ts
import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AuthService } from '@services/auth.service';
import { BehaviorSubject, lastValueFrom } from 'rxjs';

interface CompletedUtterance {
  id: string;
  text: string;
}

interface RealtimeEvent {
  type: string;
  [key: string]: any;
}

@Injectable({ providedIn: 'root' })
export class RealtimeSttService {
  private ws?: WebSocket;
  private readonly httpClient = inject(HttpClient);
  private readonly authService = inject(AuthService);
  private audioContext?: AudioContext;
  private mediaStream?: MediaStream;
  private processor?: ScriptProcessorNode;

  private readonly partialSubject = new BehaviorSubject<string>('');
  partial$ = this.partialSubject.asObservable();

  private readonly completedSubject = new BehaviorSubject<CompletedUtterance | undefined>(undefined);
  completed$ = this.completedSubject.asObservable();

  private lastCompleted?: CompletedUtterance;
  get lastCompletedUtterance() {
    return this.lastCompleted;
  }

  async startSession() {
    try {
      // Get API key from backend
      const accessToken = await this.authService.getAccessToken();
      const { apiKey } = await lastValueFrom(
        this.httpClient.post<{ apiKey: string }>(
          'https://ai.dev.k8s.api.halloalberta.de/token',
          {},
          { headers: { Authorization: `Bearer ${accessToken}` } }
        )
      );

      // Initialize audio capture
      await this.initAudioCapture();

      // Connect to OpenAI Realtime API via WebSocket (GA version)
      this.ws = new WebSocket('wss://api.openai.com/v1/realtime?model=gpt-realtime', [
        'realtime',
        `openai-insecure-api-key.${apiKey}`,
      ]);

      this.ws.addEventListener('open', () => this.onWebSocketOpen());
      this.ws.addEventListener('message', event => this.onWebSocketMessage(event));
      this.ws.addEventListener('error', error => this.onWebSocketError(error));
      this.ws.addEventListener('close', () => this.onWebSocketClose());

      console.log('Realtime session connecting...');
    } catch (e) {
      console.error('Failed to start session:', e);
      throw e;
    }
  }

  private async initAudioCapture() {
    try {
      // Request microphone access
      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: 24000,
          echoCancellation: true,
          noiseSuppression: true,
        },
      });

      // Create audio context
      this.audioContext = new AudioContext({ sampleRate: 24000 });
      const source = this.audioContext.createMediaStreamSource(this.mediaStream);

      // Create processor for audio data
      this.processor = this.audioContext.createScriptProcessor(4096, 1, 1);
      this.processor.onaudioprocess = e => this.onAudioProcess(e);

      // Connect nodes
      source.connect(this.processor);
      this.processor.connect(this.audioContext.destination);

      console.log('Audio capture initialized');
    } catch (error) {
      console.error('Failed to initialize audio capture:', error);
      throw error;
    }
  }

  private onWebSocketOpen() {
    console.log('WebSocket connected');

    // Send session configuration
    this.sendEvent({
      type: 'session.update',
      session: {
        type: 'realtime',
        output_modalities: ['text'],
        audio: {
          input: {
            transcription: { model: 'gpt-4o-transcribe', language: 'de' },
            turn_detection: {
              type: 'server_vad',
              threshold: 0.5,
              silence_duration_ms: 900,
            },
          },
        },
      },
    });
  }

  private onWebSocketMessage(event: MessageEvent) {
    try {
      const data: RealtimeEvent = JSON.parse(event.data);

      switch (data.type) {
        case 'session.created':
        case 'session.updated':
          console.log('Session ready:', data.type);
          break;

        case 'conversation.item.input_audio_transcription.delta':
          // Partial transcription
          if (data.delta) {
            const currentPartial = this.partialSubject.value + data.delta;
            this.partialSubject.next(currentPartial);
          }
          break;

        case 'conversation.item.input_audio_transcription.completed':
          // Final transcription
          if (data.transcript) {
            const utterance: CompletedUtterance = {
              id: data.item_id || this.generateId(),
              text: data.transcript,
            };
            this.lastCompleted = utterance;
            this.completedSubject.next(utterance);
            // Clear partial text
            this.partialSubject.next('');
          }
          break;

        case 'input_audio_buffer.speech_started':
          console.log('Speech detected');
          this.partialSubject.next('');
          break;

        case 'input_audio_buffer.speech_stopped':
          console.log('Speech ended');
          break;

        case 'error':
          console.error('Realtime API error:', data);
          break;

        default:
          // Ignore other event types
          break;
      }
    } catch (error) {
      console.error('Error parsing WebSocket message:', error);
    }
  }

  private onWebSocketError(error: Event) {
    console.error('WebSocket error:', error);
  }

  private onWebSocketClose() {
    console.log('WebSocket disconnected');
  }

  private onAudioProcess(event: AudioProcessingEvent) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return;
    }

    const inputData = event.inputBuffer.getChannelData(0);

    // Convert float32 audio to int16 PCM
    const pcm16 = new Int16Array(inputData.length);
    for (let i = 0; i < inputData.length; i++) {
      const s = Math.max(-1, Math.min(1, inputData[i]));
      pcm16[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
    }

    // Convert to base64
    const base64Audio = this.arrayBufferToBase64(pcm16.buffer);

    // Send audio data to API
    this.sendEvent({
      type: 'input_audio_buffer.append',
      audio: base64Audio,
    });
  }

  private sendEvent(event: any) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(event));
    }
  }

  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  private generateId(): string {
    return `utterance_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  stopSession() {
    // Stop audio processing
    if (this.processor) {
      this.processor.disconnect();
      this.processor = undefined;
    }

    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = undefined;
    }

    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach(track => track.stop());
      this.mediaStream = undefined;
    }

    // Close WebSocket
    if (this.ws) {
      this.ws.close();
      this.ws = undefined;
    }

    // Clear observables
    this.partialSubject.next('');
  }
}
